import { useCallback, useEffect, useMemo, useState } from 'react';

import { mergeColumnVisibility } from '@/utils/columnVisibility';
import { getFromStorage, setToStorage } from '@/utils/localStorage';

import {
    type AuditColumnId,
    type AuditColumnVisibility,
    type AuditHideableColumnId,
    DEFAULT_AUDIT_COLUMN_VISIBILITY,
    HIDEABLE_AUDIT_COLUMNS,
    isHideableAuditColumn,
    visibleAuditColumns,
} from './table/auditLogColumns';

export const AUDIT_COLUMN_VISIBILITY_STORAGE_KEY = 'audit-log-column-visibility';

export interface AuditColumnVisibilityState {
    visibility: AuditColumnVisibility;
    /** The columns to render, in table order. */
    columns: AuditColumnId[];
    setVisibleColumns: (visible: readonly AuditHideableColumnId[]) => void;
}

/**
 * Storage holds whatever an older version of the page or a hand edit left there, so only
 * a boolean for a column that can be hidden counts. Layering it over the defaults gives a
 * column added later its default instead of switching it on.
 */
export function resolveAuditColumnVisibility(stored: unknown): AuditColumnVisibility {
    const choices: Record<string, boolean> = {};
    if (typeof stored === 'object' && stored !== null && !Array.isArray(stored)) {
        for (const [id, visible] of Object.entries(stored)) {
            if (isHideableAuditColumn(id) && typeof visible === 'boolean') choices[id] = visible;
        }
    }
    const merged = mergeColumnVisibility(DEFAULT_AUDIT_COLUMN_VISIBILITY, choices);
    const visibility: AuditColumnVisibility = { ...DEFAULT_AUDIT_COLUMN_VISIBILITY };
    for (const id of HIDEABLE_AUDIT_COLUMNS) visibility[id] = merged[id] === true;
    return visibility;
}

/** The Columns menu's choice, remembered per browser like the Cages page's. */
export function useAuditColumnVisibility(): AuditColumnVisibilityState {
    const [visibility, setVisibility] = useState<AuditColumnVisibility>(() =>
        resolveAuditColumnVisibility(
            getFromStorage<unknown>(AUDIT_COLUMN_VISIBILITY_STORAGE_KEY, {})
        )
    );

    useEffect(() => {
        setToStorage(AUDIT_COLUMN_VISIBILITY_STORAGE_KEY, visibility);
    }, [visibility]);

    const columns = useMemo(() => visibleAuditColumns(visibility), [visibility]);

    const setVisibleColumns = useCallback((visible: readonly AuditHideableColumnId[]) => {
        const next: AuditColumnVisibility = { ...DEFAULT_AUDIT_COLUMN_VISIBILITY };
        for (const id of HIDEABLE_AUDIT_COLUMNS) next[id] = visible.includes(id);
        setVisibility(next);
    }, []);

    return { visibility, columns, setVisibleColumns };
}
